import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { LogOut, Calendar, Image as ImageIcon, ArrowRight, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import BrandLogo from '../components/BrandLogo';
import { clientApi } from '../api';

const ClientHome = () => {
  const { clientSlug } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      if (!clientApi.hasToken(clientSlug)) { nav(`/c/${clientSlug}`, { replace: true }); return; }
      try {
        const d = await clientApi.me(clientSlug);
        setData(d);
      } catch (err) {
        clientApi.logout(clientSlug);
        nav(`/c/${clientSlug}`, { replace: true });
      }
    })();
  }, [clientSlug, nav]);

  const logout = () => { clientApi.logout(clientSlug); nav(`/c/${clientSlug}`); };

  if (!data) return null;
  const totalPhotos = data.galleries.reduce((s, g) => s + (g.photo_count || 0), 0);

  return (
    <div className="min-h-screen bg-[#0B0B0D] text-white">
      <header className="border-b border-white/10 bg-black/40 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2">
            <BrandLogo size="sm" />
          </Link>
          <div className="ml-auto flex items-center gap-4">
            <span className="text-xs text-slate-400 hidden sm:inline">Benvenuto/a, <b className="text-white">{data.display_name}</b></span>
            <button onClick={logout} className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1.5"><LogOut className="w-3.5 h-3.5" /> Esci</button>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-[#C9A961]/10 rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-24">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-[#C9A961]/30 text-[#C9A961] text-[11px] uppercase tracking-[0.25em] mb-6">
            <Sparkles className="w-3.5 h-3.5" /> La tua area riservata
          </div>
          <h1 className="font-display text-5xl md:text-7xl italic mb-4">{data.name}</h1>
          <p className="text-slate-400 max-w-2xl">
            {data.galleries.length} {data.galleries.length === 1 ? 'lavoro' : 'lavori'} · {totalPhotos} foto totali. Clicca su un lavoro per visualizzare, commentare e scaricare le foto.
          </p>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 md:px-6 pb-20">
        {data.galleries.length === 0 ? (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-12 text-center">
            <ImageIcon className="w-10 h-10 text-[#C9A961] mx-auto mb-3" />
            <p className="text-slate-300">Nicola non ha ancora caricato lavori per te. Riprova tra qualche giorno.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.galleries.map((g, idx) => (
              <Link key={g.id} to={`/c/${clientSlug}/lavori/${g.slug}`} className="group relative rounded-2xl overflow-hidden bg-white/5 border border-white/10 hover:border-[#C9A961]/50 transition-all duration-500">
                <div className="aspect-[4/5] overflow-hidden bg-black">
                  {g.cover_url ? <img src={g.cover_url} alt={g.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /> : <div className="w-full h-full grid place-items-center text-slate-600"><ImageIcon /></div>}
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.25em] text-[#C9A961]">Lavoro {idx + 1}</div>
                <div className="absolute bottom-0 inset-x-0 p-5">
                  <div className="flex items-center gap-3 text-[11px] text-slate-300 mb-2">
                    <span className="px-2 py-0.5 rounded-full bg-white/10 border border-white/10">{g.category}</span>
                    {g.date && <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {g.date}</span>}
                    <span className="inline-flex items-center gap-1"><ImageIcon className="w-3 h-3" /> {g.photo_count}</span>
                  </div>
                  <h3 className="font-display text-2xl text-white">{g.title}</h3>
                  <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#C9A961] font-semibold">
                    Apri galleria <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default ClientHome;
